import React from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';
import { isApiError } from '@/services/api';
import { useAddressFind } from './useAddressFind';
import { useAddressRetrieve } from './useAddressRetrieve';
import { deriveStatus } from '../components/AddressLookup/deriveStatus';
import { ADDRESS_LOOKUP_COPY } from '../components/AddressLookup/copy';
import type { AddressLookupResult, NormalisedAddress } from '../types/addressLookup.types';

// Debounced as ONE object so a container drill-down can never issue a
// request for the pair (oldText, newContainer).
interface AddressQuery {
    text: string;
    container?: string;
}

export interface UseAddressLookupParams {
    onAddressSelected: (address: NormalisedAddress) => void;
    disabled: boolean;
    minChars: number;
}

export const useAddressLookup = ({
    onAddressSelected,
    disabled,
    minChars,
}: UseAddressLookupParams) => {
    const [query, setQuery] = React.useState<AddressQuery>({ text: '' });
    const [open, setOpen] = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(0);
    const [filled, setFilled] = React.useState(false);
    const [retrieveError, setRetrieveError] = React.useState<unknown>(undefined);

    const debounced = useDebounce(query, 300);
    const inputRef = React.useRef<HTMLInputElement>(null);
    // Bumped by every action that moves the agent on; select() discards a settled Retrieve whose token is stale.
    const retrieveToken = React.useRef(0);
    const supersedeRetrieve = () => {
        retrieveToken.current += 1;
        if (retrieveError !== undefined) setRetrieveError(undefined);
        return retrieveToken.current;
    };
    // Ref-tracked and cleared on unmount, unlike SearchableSelect's uncleaned blur timer.
    const blurTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reads the debounced value, never the live one, so the gate can never disagree with the key.
    const canQuery =
        !disabled &&
        (debounced.container !== undefined || debounced.text.trim().length >= minChars);

    const { data, isFetching, error, isSuccess } = useAddressFind({
        text: debounced.text,
        container: debounced.container,
        enabled: canQuery,
    });
    const retrieve = useAddressRetrieve();

    const results = data ?? [];
    const activeIndex = results.length ? Math.min(Math.max(highlighted, 0), results.length - 1) : 0;

    React.useEffect(() => {
        setHighlighted(0);
    }, [query, debounced]);

    React.useEffect(() => {
        return () => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
        };
    }, []);

    const activeError = error ?? retrieveError;
    const activeErrorStatus = isApiError(activeError) ? activeError.status : undefined;

    const statusText = deriveStatus({
        isSearching: retrieve.isPending || isFetching,
        hasError: Boolean(activeError),
        errorStatus: activeErrorStatus,
        isSuccess,
        resultsLength: results.length,
        filled,
        canQuery,
        disabled,
        hasContainer: query.container !== undefined,
        minChars,
    });

    const select = async (row: AddressLookupResult) => {
        setFilled(false);

        if (row.type === 'container') {
            supersedeRetrieve();
            setQuery({ text: '', container: row.id });
            setOpen(true);
            inputRef.current?.focus();
            return;
        }

        const token = supersedeRetrieve();
        try {
            const address = await retrieve.mutateAsync(row.id);
            if (token !== retrieveToken.current) return;
            onAddressSelected(address);
            setQuery({ text: '', container: undefined });
            setOpen(false);
            setFilled(true);
        } catch (err) {
            if (token !== retrieveToken.current) return;
            setRetrieveError(err);
            setOpen(false);
        }
    };

    const clear = () => {
        setQuery({ text: '', container: undefined });
        setFilled(false);
        supersedeRetrieve();
        inputRef.current?.focus();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilled(false);
        supersedeRetrieve();
        setQuery((q) => ({ text: e.target.value, container: q.container }));
        setOpen(true);
    };

    const handleFocus = () => {
        if (disabled) return;
        if (blurTimer.current) {
            clearTimeout(blurTimer.current);
            blurTimer.current = null;
        }
        setOpen(true);
    };

    const handleBlur = () => {
        if (blurTimer.current) clearTimeout(blurTimer.current);
        blurTimer.current = setTimeout(() => {
            setOpen(false);
        }, 150);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                // Both branches preventDefault Enter, or it submits the host form.
                if (e.key === 'Enter') e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(Math.min(activeIndex + 1, Math.max(results.length - 1, 0)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(Math.max(activeIndex - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[activeIndex]) void select(results[activeIndex]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const showClear = !disabled && (query.text !== '' || query.container !== undefined);
    const showListbox = open && !disabled;
    // filled belongs to the status region only; the listbox falls through so a reopened dropdown is never empty.
    const listboxPlaceholder =
        statusText === ADDRESS_LOOKUP_COPY.filled
            ? ADDRESS_LOOKUP_COPY.minChars(minChars)
            : statusText;

    return {
        inputRef,
        query,
        isRetrieving: retrieve.isPending,
        results,
        activeIndex,
        statusText,
        showClear,
        showListbox,
        listboxPlaceholder,
        setHighlighted,
        select,
        clear,
        handleChange,
        handleFocus,
        handleBlur,
        handleKeyDown,
    };
};
